import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Plus, Mail, UserCircle, Pencil, Trash2, AlertTriangle, Search, Users as UsersIcon, Briefcase, Building2, Eye, Package, FileText, DollarSign, Bell, LayoutGrid, List, Clock, CheckCircle, XCircle, Filter } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/dateFormat';
import { ManagePermissionsDialog } from '@/components/settings/ManagePermissionsDialog';
import { useModuleAccess } from '@/hooks/usePlanPermissions';
import { ShieldCheck, Crown } from 'lucide-react';
import { FoundingAccessPanel } from '@/components/billing/FoundingAccessPanel';

const MANAGED_ROLES = ['owner', 'admin', 'staff', 'project_manager', 'designer', 'site_engineer'];
const RoleOrPermissions = ({ u, canManage, onManage, getRoleBadge, formatRoleName }) => (
  canManage && MANAGED_ROLES.includes(u.role) ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onManage(u); }}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-white/60 backdrop-blur border border-stone-200 text-stone-700 hover:bg-stone-900 hover:text-white transition-colors"
      title={formatRoleName(u.role)}
      data-testid={`manage-permissions-btn-${u.id}`}
    >
      <ShieldCheck size={12} />Manage Permissions
    </button>
  ) : (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(u.role)}`}>{formatRoleName(u.role)}</span>
  )
);

// Helper function - defined outside component to avoid hoisting issues
const formatRoleName = (role) => {
  const roleNames = {
    owner: 'Owner',
    admin: 'Admin',
    project_manager: 'Project Manager',
    designer: 'Designer',
    site_engineer: 'Site Engineer',
    staff: 'Staff',
    client: 'Client',
    vendor: 'Vendor',
    labour: 'Labour',
    consultant: 'Consultant',
    architect: 'Architect',
    interior_designer: 'Interior Designer',
    external_designer: 'External Designer',
  };
  return roleNames[role] || (role ? role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ') : 'Unknown');
};

// Client-type roles (all route to Client Profile)
const CLIENT_TYPE_ROLES = ['client', 'architect', 'interior_designer', 'consultant', 'external_designer'];

// Get category for a role
const getRoleCategory = (role) => {
  if (CLIENT_TYPE_ROLES.includes(role)) return 'clients';
  if (['vendor', 'labour'].includes(role)) return 'vendors';
  if (['owner', 'admin', 'staff', 'project_manager', 'designer', 'site_engineer'].includes(role)) return 'bellacasa';
  return 'other';
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('bellacasa');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list' (Windows-style detailed view) - Default to list
  const [sortBy, setSortBy] = useState('name'); // 'name', 'role', 'last_login', 'created_at'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  
  // Preview states
  const [showClientPreview, setShowClientPreview] = useState(false);
  const [previewClient, setPreviewClient] = useState(null);
  const [clientPreviewData, setClientPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  const [showVendorPreview, setShowVendorPreview] = useState(false);
  const [previewVendor, setPreviewVendor] = useState(null);
  const [vendorPreviewData, setVendorPreviewData] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { can_manage_permissions: canManagePerms, is_platform_owner: isPlatformOwner } = useModuleAccess();
  const [foundingOpen, setFoundingOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'client',
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    role: '',
    password: '',
  });

  // Master data state for clients/vendors
  const [masterDataOpen, setMasterDataOpen] = useState(false);
  const [masterData, setMasterData] = useState({
    company_name: '',
    gst_number: '',
    pan_number: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_pincode: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    bank_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    payment_terms: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch master data for a user
  const fetchMasterData = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/master-data`);
      setMasterData(response.data.master_data || {});
    } catch (error) {
      console.error('Failed to fetch master data:', error);
      setMasterData({});
    }
  };

  // Save master data
  const handleSaveMasterData = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.put(`${API}/users/${selectedUser.id}/master-data`, masterData);
      toast.success('Master data saved successfully');
      setMasterDataOpen(false);
    } catch (error) {
      toast.error('Failed to save master data');
    }
  };

  // Open master data dialog
  const handleOpenMasterData = async (user) => {
    setSelectedUser(user);
    await fetchMasterData(user.id);
    setMasterDataOpen(true);
  };

  // Preview as Client
  const handleClientPreview = async (client) => {
    setPreviewClient(client);
    setLoadingPreview(true);
    setShowClientPreview(true);
    
    try {
      const response = await axios.get(`${API}/admin/client-profile/${client.id}`);
      setClientPreviewData(response.data);
    } catch (error) {
      toast.error('Failed to load client preview');
      setShowClientPreview(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Preview as Vendor
  const handleVendorPreview = async (vendor) => {
    setPreviewVendor(vendor);
    setLoadingPreview(true);
    setShowVendorPreview(true);
    
    try {
      const response = await axios.get(`${API}/admin/vendor-profile/${vendor.id}`);
      setVendorPreviewData(response.data);
    } catch (error) {
      toast.error('Failed to load vendor preview');
      setShowVendorPreview(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Filter users by tab and search
  const filteredUsers = useMemo(() => {
    let filtered = users;
    
    // Filter by tab
    if (activeTab === 'clients') {
      filtered = filtered.filter(u => CLIENT_TYPE_ROLES.includes(u.role));
    } else if (activeTab === 'vendors') {
      filtered = filtered.filter(u => u.role === 'vendor' || u.role === 'labour');
    } else if (activeTab === 'bellacasa') {
      filtered = filtered.filter(u => ['owner', 'admin', 'staff', 'project_manager', 'designer', 'site_engineer'].includes(u.role));
    }
    
    // Filter by search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.role?.toLowerCase().includes(term) ||
        formatRoleName(u.role)?.toLowerCase().includes(term) ||
        u.organization?.toLowerCase().includes(term)
      );
    }
    
    // Sort
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'role':
          comparison = (a.role || '').localeCompare(b.role || '');
          break;
        case 'last_login':
          comparison = new Date(b.last_login || 0) - new Date(a.last_login || 0);
          break;
        case 'created_at':
          comparison = new Date(b.created_at || 0) - new Date(a.created_at || 0);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [users, activeTab, searchTerm, sortBy, sortOrder]);

  // Count users by category
  const userCounts = useMemo(() => ({
    all: users.length,
    clients: users.filter(u => CLIENT_TYPE_ROLES.includes(u.role)).length,
    vendors: users.filter(u => u.role === 'vendor' || u.role === 'labour').length,
    bellacasa: users.filter(u => ['owner', 'admin', 'staff', 'project_manager', 'designer', 'site_engineer'].includes(u.role)).length,
  }), [users]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success('User created successfully');
      setDialogOpen(false);
      fetchUsers();
      setFormData({
        email: '',
        password: '',
        name: '',
        role: 'client',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEditClick = (u) => {
    setSelectedUser(u);
    setEditFormData({
      name: u.name,
      role: u.role,
      password: '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const updateData = { name: editFormData.name, role: editFormData.role };
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }
      
      await axios.put(`${API}/users/${selectedUser.id}`, updateData);
      // iter-308: if admin reset the password, also force-logout all of that
      // user's active sessions.
      if (editFormData.password) {
        try { await axios.post(`${API}/users/${selectedUser.id}/logout-everywhere`); } catch {}
        toast.success(`Password reset for ${selectedUser.name}. All their sessions have been revoked.`);
      } else {
        toast.success('User updated successfully');
      }
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // iter-308: explicit "Logout from everywhere" — invalidates JWTs for the
  // target user without changing the password. Owner/Admin can do it for any
  // staff/admin user; current user can also self-revoke (forces them to
  // re-login on every device including this one).
  const handleLogoutEverywhere = async (u) => {
    const isSelf = user?.id === u.id;
    const msg = isSelf
      ? 'Sign out from EVERY device including this one?\n\nYou will be logged out immediately and need to sign in again.'
      : `Force-sign-out ${u.name} from every device?\n\nThey will need to log in again with their current password.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.post(`${API}/users/${u.id}/logout-everywhere`);
      if (isSelf) {
        toast.success('All sessions revoked. Redirecting to login…');
        setTimeout(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }, 800);
      } else {
        toast.success(`All sessions for ${u.name} revoked.`);
        setEditDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke sessions');
    }
  };

  const handleDeleteClick = (u) => {
    setSelectedUser(u);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axios.delete(`${API}/users/${selectedUser.id}`);
      const orphanedTasks = response.data.orphaned_tasks;
      if (orphanedTasks > 0) {
        toast.success(`User deleted. ${orphanedTasks} task(s) are now unassigned.`);
      } else {
        toast.success('User deleted successfully');
      }
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: 'bg-primary/10 text-stone-800',
      admin: 'bg-indigo-500/10 text-indigo-600',
      project_manager: 'bg-sky-500/10 text-sky-600',
      designer: 'bg-pink-500/10 text-pink-600',
      site_engineer: 'bg-teal-500/10 text-teal-600',
      staff: 'bg-blue-500/10 text-blue-600',
      client: 'bg-green-500/10 text-green-600',
      vendor: 'bg-orange-500/10 text-orange-600',
      labour: 'bg-purple-500/10 text-purple-600',
      consultant: 'bg-cyan-500/10 text-cyan-600',
      architect: 'bg-amber-500/10 text-amber-600',
      interior_designer: 'bg-fuchsia-500/10 text-fuchsia-600',
      external_designer: 'bg-rose-500/10 text-rose-600',
    };
    return styles[role] || styles.client;
  };

  // All available roles for selection
  const availableRoles = [
    { value: 'owner', label: 'Owner', description: 'Full access to everything' },
    { value: 'admin', label: 'Admin', description: 'Almost full access (cannot modify owners)' },
    { value: 'project_manager', label: 'Project Manager', description: 'Manage projects and tasks' },
    { value: 'designer', label: 'Designer', description: 'Design-related access' },
    { value: 'site_engineer', label: 'Site Engineer', description: 'Site-related access' },
    { value: 'staff', label: 'Staff', description: 'General staff access' },
    { value: 'client', label: 'Client', description: 'View assigned projects and invoices' },
    { value: 'architect', label: 'Architect', description: 'Professional with project tracking access' },
    { value: 'interior_designer', label: 'Interior Designer', description: 'Professional with project tracking access' },
    { value: 'consultant', label: 'Consultant', description: 'Professional with project tracking access' },
    { value: 'vendor', label: 'Vendor', description: 'View and update assigned tasks' },
    { value: 'labour', label: 'Labour', description: 'View and update assigned tasks' },
    { value: 'external_designer', label: 'External Designer', description: 'External designer with project tracking access' },
  ];

  const canEditUser = (targetUser) => {
    if (user.role === 'owner') return true;
    if (user.role === 'staff' && targetUser.role !== 'owner') return true;
    return false;
  };

  const canDeleteUser = (targetUser) => {
    return user.role === 'owner' && targetUser.id !== user.id;
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12" data-testid="loading-state">Loading...</div>
      </Layout>
    );
  }

  // Render user card
  const renderUserCard = (u) => (
    <div
      key={u.id}
      className="apple-card p-6 group hover:shadow-md transition-all"
      data-testid={`user-card-${u.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
            <UserCircle size={32} className="text-stone-400" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900">{u.name}</h3>
            <RoleOrPermissions u={u} canManage={canManagePerms} onManage={setPermUser} getRoleBadge={getRoleBadge} formatRoleName={formatRoleName} />
          </div>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {canEditUser(u) && (
            <button
              onClick={() => handleEditClick(u)}
              className="p-2 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
              data-testid={`edit-user-${u.id}`}
            >
              <Pencil size={16} />
            </button>
          )}
          {canDeleteUser(u) && (
            <button
              onClick={() => handleDeleteClick(u)}
              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
              data-testid={`delete-user-${u.id}`}
            >
              <Trash2 size={16} />
            </button>
          )}
          {/* Preview Button for Clients and Vendors */}
          {CLIENT_TYPE_ROLES.includes(u.role) && (
            <button
              onClick={() => handleClientPreview(u)}
              className="p-2 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
              title="Preview as Client"
              data-testid={`preview-client-${u.id}`}
            >
              <Eye size={16} />
            </button>
          )}
          {(u.role === 'vendor' || u.role === 'labour') && (
            <button
              onClick={() => handleVendorPreview(u)}
              className="p-2 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
              title="Preview as Vendor"
              data-testid={`preview-vendor-${u.id}`}
            >
              <Eye size={16} />
            </button>
          )}
        </div>
      </div>
      <div className="space-y-2 text-sm text-stone-500">
        <div className="flex items-center gap-2">
          <Mail size={14} />
          <span className="truncate">{u.email}</span>
        </div>
        {u.phone && (
          <div className="flex items-center gap-2">
            <span>📞</span>
            <span>{u.phone}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Layout>
      <div data-testid="users-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-5xl font-bold tracking-tight text-stone-900 mb-2" data-testid="users-title">Users</h1>
            <p className="text-stone-500">Manage team members, clients, and vendors</p>
          </div>
          <div className="flex items-center gap-2">
          {isPlatformOwner && (
            <Button variant="outline" className="rounded-full gap-2" onClick={() => setFoundingOpen(true)} data-testid="founding-access-open-btn">
              <Crown size={16} className="text-amber-500" />Founding Access
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="apple-button flex items-center gap-2" data-testid="create-user-button">
                <Plus size={20} />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-user-dialog">
              <DialogHeader>
                <DialogTitle className="font-manrope text-xl text-stone-900">Add New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-stone-900">Full Name</Label>
                  <Input
                    required
                    className="bg-white text-stone-900 border-stone-300"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="name-input"
                  />
                </div>

                <div>
                  <Label className="text-stone-900">Email</Label>
                  <Input
                    type="email"
                    required
                    className="bg-white text-stone-900 border-stone-300"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label className="text-stone-900">Password</Label>
                  <Input
                    type="password"
                    required
                    className="bg-white text-stone-900 border-stone-300"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="password-input"
                  />
                </div>

                <div>
                  <Label className="text-stone-900">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger className="bg-white text-stone-900 border-stone-300" data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoles
                        .filter(r => user.role === 'owner' || r.value !== 'owner')
                        .map(r => (
                          <SelectItem key={r.value} value={r.value}>
                            <div className="flex flex-col">
                              <span>{r.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 justify-end">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="text-stone-900">
                    Cancel
                  </Button>
                  <Button type="submit" className="apple-button" data-testid="submit-user-button">
                    Add User
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Search Bar and View Controls */}
        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <Input
              placeholder="Search by name, email, role, or organization..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
              data-testid="search-users"
            />
          </div>
          
          <div className="flex items-center gap-3">
            {/* Sort Options */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white"
              data-testid="sort-by-select"
            >
              <option value="name">Sort by Name</option>
              <option value="role">Sort by Role</option>
              <option value="created_at">Sort by Join Date</option>
              <option value="last_login">Sort by Last Login</option>
            </select>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-600"
              title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
            
            {/* View Mode Toggle */}
            <div className="flex items-center bg-stone-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}
                title="Card View"
                data-testid="view-mode-grid"
              >
                <LayoutGrid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}
                title="Detailed List View"
                data-testid="view-mode-list"
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-stone-100 p-1 h-auto flex flex-wrap">
            <TabsTrigger 
              value="bellacasa" 
              className="gap-2 text-stone-600 data-[state=active]:bg-white data-[state=active]:text-stone-800 data-[state=active]:shadow-sm"
              data-testid="tab-bellacasa"
            >
              <Building2 size={16} />
              Bella Casa Team ({userCounts.bellacasa})
            </TabsTrigger>
          </TabsList>

          {/* Tab Content - Users Grid or List */}
          <TabsContent value={activeTab} className="mt-6">
            {filteredUsers.length === 0 ? (
              <div className="apple-card p-16 text-center">
                <UsersIcon size={48} className="mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500">No users found</p>
                <p className="text-sm text-stone-400 mt-1">
                  {searchTerm ? 'Try adjusting your search' : 'Add users to see them here'}
                </p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="users-grid">
                {filteredUsers.map(renderUserCard)}
              </div>
            ) : (
              /* Detailed List View (Windows-style) */
              <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="users-list">
                {/* Header Row */}
                <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-stone-50 border-b border-stone-200 text-xs font-medium text-stone-500 uppercase tracking-wider">
                  <div className="col-span-3">User</div>
                  <div className="col-span-2">Role</div>
                  <div className="col-span-3">Email</div>
                  <div className="col-span-2">Last Activity</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1 text-right">Actions</div>
                </div>
                {/* User Rows */}
                {filteredUsers.map((u) => (
                  <div 
                    key={u.id} 
                    className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-stone-100 hover:bg-stone-50 transition-colors items-center cursor-pointer"
                    onClick={() => { setSelectedUser(u); setEditFormData({ name: u.name, role: u.role, password: '', phone: u.phone || '' }); setEditDialogOpen(true); }}
                    data-testid={`user-row-${u.id}`}
                  >
                    {/* User Info */}
                    <div className="col-span-3 flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-stone-200 to-stone-300 flex items-center justify-center flex-shrink-0">
                        <UserCircle size={20} className="text-stone-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-stone-900 truncate">{u.name}</p>
                        {u.organization && (
                          <p className="text-xs text-stone-400 truncate">{u.organization}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Role */}
                    <div className="col-span-2">
                      <RoleOrPermissions u={u} canManage={canManagePerms} onManage={setPermUser} getRoleBadge={getRoleBadge} formatRoleName={formatRoleName} />
                    </div>
                    
                    {/* Email */}
                    <div className="col-span-3 text-sm text-stone-600 truncate">
                      {u.email}
                    </div>
                    
                    {/* Last Activity */}
                    <div className="col-span-2 text-sm text-stone-500">
                      {u.last_login ? (
                        <div className="flex items-center gap-1">
                          <Clock size={12} />
                          <span>{formatDate(new Date(u.last_login))}</span>
                        </div>
                      ) : (
                        <span className="text-stone-400">Never</span>
                      )}
                    </div>
                    
                    {/* Status */}
                    <div className="col-span-1">
                      {u.email_verified !== false ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600">
                          <CheckCircle size={12} />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                          <XCircle size={12} />
                          Pending
                        </span>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="col-span-1 flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => { setSelectedUser(u); setEditFormData({ name: u.name, role: u.role, password: '', phone: u.phone || '' }); setEditDialogOpen(true); }}
                        className="p-1.5 text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                      {(CLIENT_TYPE_ROLES.includes(u.role) || u.role === 'vendor') && (
                        <button
                          onClick={() => handleOpenMasterData(u)}
                          className="p-1.5 text-stone-400 hover:text-green-600 hover:bg-green-50 rounded"
                          title="Business Details (GST, Company)"
                        >
                          <Building2 size={14} />
                        </button>
                      )}
                      {u.id !== user.id && (
                        <button
                          onClick={() => handleDeleteClick(u)}
                          className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      {CLIENT_TYPE_ROLES.includes(u.role) && (
                        <button
                          onClick={() => handleClientPreview(u)}
                          className="p-1.5 text-stone-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                          title="Preview"
                        >
                          <Eye size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent data-testid="edit-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl">Edit User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  required
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  data-testid="edit-name-input"
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  disabled
                  value={selectedUser?.email || ''}
                  className="bg-stone-50"
                  data-testid="edit-email-input"
                />
                <p className="text-xs text-stone-500 mt-1">Email cannot be changed</p>
              </div>

              <div>
                <Label>New Password (leave blank to keep current)</Label>
                <Input
                  type="password"
                  value={editFormData.password}
                  onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="edit-password-input"
                />
              </div>

              <div>
                <Label>Role</Label>
                <Select
                  value={editFormData.role}
                  onValueChange={(value) => setEditFormData({ ...editFormData, role: value })}
                  disabled={selectedUser?.id === user.id}
                >
                  <SelectTrigger data-testid="edit-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles
                      .filter(r => user.role === 'owner' || r.value !== 'owner')
                      .map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          <div className="flex flex-col">
                            <span>{r.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {selectedUser?.id === user.id && (
                  <p className="text-xs text-amber-600 mt-1">You cannot change your own role</p>
                )}
              </div>

              {/* iter-308: Logout from everywhere — for admin/owner/staff users */}
              {selectedUser && ['admin', 'owner', 'staff'].includes(selectedUser.role) && (
                <div className="rounded-md border border-red-200 bg-red-50/50 p-3 space-y-2">
                  <p className="text-xs text-red-900 font-semibold flex items-center gap-1">
                    <AlertTriangle size={13} className="text-red-600" />
                    Security: Sign out from all devices
                  </p>
                  <p className="text-[11px] text-red-800 leading-relaxed">
                    Invalidates every active session for this user across every device they're signed in on. They'll need to log in again with their current password.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-red-400 text-red-700 hover:bg-red-100"
                    onClick={() => handleLogoutEverywhere(selectedUser)}
                    data-testid="logout-everywhere-btn"
                  >
                    {selectedUser.id === user.id ? 'Sign out from every device (including this)' : `Force-sign-out ${selectedUser.name} from every device`}
                  </Button>
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="submit-edit-button">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-user-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="text-red-500" size={20} />
                Delete User
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{selectedUser?.name}</strong>? 
                This action cannot be undone. Any tasks assigned to this user will become unassigned.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-button"
              >
                Delete User
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Master Data Dialog - Admin-editable user business details */}
        <Dialog open={masterDataOpen} onOpenChange={setMasterDataOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="master-data-dialog">
            <DialogHeader>
              <DialogTitle className="font-playfair text-xl flex items-center gap-2">
                <Building2 className="text-brand-primary" size={20} />
                Business Details - {selectedUser?.name}
              </DialogTitle>
              <p className="text-sm text-stone-500">
                Admin-controlled data used in invoices, quotations, and BOQs
              </p>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Company Info */}
              <div className="space-y-4">
                <h4 className="font-semibold text-stone-900 border-b pb-2">Company Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company / Firm Name</Label>
                    <Input
                      value={masterData.company_name || ''}
                      onChange={(e) => setMasterData({ ...masterData, company_name: e.target.value })}
                      placeholder="ABC Interiors Pvt. Ltd."
                    />
                  </div>
                  <div>
                    <Label>GST Number</Label>
                    <Input
                      value={masterData.gst_number || ''}
                      onChange={(e) => setMasterData({ ...masterData, gst_number: e.target.value })}
                      placeholder="29ABCDE1234F1Z5"
                    />
                  </div>
                  <div>
                    <Label>PAN Number</Label>
                    <Input
                      value={masterData.pan_number || ''}
                      onChange={(e) => setMasterData({ ...masterData, pan_number: e.target.value })}
                      placeholder="ABCDE1234F"
                    />
                  </div>
                  <div>
                    <Label>Payment Terms</Label>
                    <Input
                      value={masterData.payment_terms || ''}
                      onChange={(e) => setMasterData({ ...masterData, payment_terms: e.target.value })}
                      placeholder="Net 30 / 50% Advance"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="space-y-4">
                <h4 className="font-semibold text-stone-900 border-b pb-2">Billing Address</h4>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={masterData.billing_address || ''}
                    onChange={(e) => setMasterData({ ...masterData, billing_address: e.target.value })}
                    placeholder="123, Main Street, Bangalore"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={masterData.billing_city || ''}
                      onChange={(e) => setMasterData({ ...masterData, billing_city: e.target.value })}
                      placeholder="Bangalore"
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={masterData.billing_state || ''}
                      onChange={(e) => setMasterData({ ...masterData, billing_state: e.target.value })}
                      placeholder="Karnataka"
                    />
                  </div>
                  <div>
                    <Label>Pincode</Label>
                    <Input
                      value={masterData.billing_pincode || ''}
                      onChange={(e) => setMasterData({ ...masterData, billing_pincode: e.target.value })}
                      placeholder="560001"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Person */}
              <div className="space-y-4">
                <h4 className="font-semibold text-stone-900 border-b pb-2">Contact Person</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={masterData.contact_person || ''}
                      onChange={(e) => setMasterData({ ...masterData, contact_person: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={masterData.contact_phone || ''}
                      onChange={(e) => setMasterData({ ...masterData, contact_phone: e.target.value })}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={masterData.contact_email || ''}
                      onChange={(e) => setMasterData({ ...masterData, contact_email: e.target.value })}
                      placeholder="contact@company.com"
                    />
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="space-y-4">
                <h4 className="font-semibold text-stone-900 border-b pb-2">Bank Details</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={masterData.bank_name || ''}
                      onChange={(e) => setMasterData({ ...masterData, bank_name: e.target.value })}
                      placeholder="HDFC Bank"
                    />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      value={masterData.bank_account_number || ''}
                      onChange={(e) => setMasterData({ ...masterData, bank_account_number: e.target.value })}
                      placeholder="50100123456789"
                    />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input
                      value={masterData.bank_ifsc || ''}
                      onChange={(e) => setMasterData({ ...masterData, bank_ifsc: e.target.value })}
                      placeholder="HDFC0001234"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end border-t pt-4">
              <Button type="button" variant="outline" onClick={() => setMasterDataOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveMasterData} className="btn-primary" data-testid="save-master-data-btn">
                Save Business Details
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Client Preview Sheet */}
        <Sheet open={showClientPreview} onOpenChange={setShowClientPreview}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Building2 size={20} className="text-blue-600" />
                Preview as Client
              </SheetTitle>
              <SheetDescription className="text-stone-500">
                Viewing exactly what {previewClient?.name || 'client'} sees in their profile
              </SheetDescription>
            </SheetHeader>

            {loadingPreview ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : clientPreviewData ? (
              <div className="mt-6 space-y-6">
                {/* Client Info */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-900">{clientPreviewData.client?.name}</h3>
                  <p className="text-sm text-blue-700">{clientPreviewData.client?.email}</p>
                  <p className="text-sm text-blue-600">{clientPreviewData.client?.phone}</p>
                  <Badge className="mt-2 bg-blue-100 text-blue-700">
                    {clientPreviewData.client?.client_type || 'Residential'}
                  </Badge>
                </div>

                {/* Financial Summary */}
                <div>
                  <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                    <DollarSign size={16} />
                    Financial Summary
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500">Total Quoted</p>
                      <p className="text-lg font-bold">₹{(clientPreviewData.financial_summary?.total_quoted || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500">Total Invoiced</p>
                      <p className="text-lg font-bold">₹{(clientPreviewData.financial_summary?.total_invoiced || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-xs text-green-600">Paid</p>
                      <p className="text-lg font-bold text-green-600">₹{(clientPreviewData.financial_summary?.total_paid || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="text-xs text-amber-600">Pending</p>
                      <p className="text-lg font-bold text-amber-600">₹{(clientPreviewData.financial_summary?.total_pending || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Projects */}
                <div>
                  <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                    <Briefcase size={16} />
                    Projects ({clientPreviewData.projects?.length || 0})
                  </h4>
                  {clientPreviewData.projects?.length > 0 ? (
                    <div className="space-y-2">
                      {clientPreviewData.projects.map(project => (
                        <div key={project.id} className="p-3 bg-stone-50 rounded-lg">
                          <p className="font-medium">{project.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline">{project.status}</Badge>
                            <span className="text-sm text-stone-500">₹{project.budget?.toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-stone-500 italic">No projects yet</p>
                  )}
                </div>

                {/* Recent Quotations */}
                {clientPreviewData.quotations?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                      <FileText size={16} />
                      Recent Quotations
                    </h4>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {clientPreviewData.quotations.slice(0, 5).map(quot => (
                        <div key={quot.id} className="p-3 bg-stone-50 rounded-lg flex justify-between items-center">
                          <div>
                            <p className="font-medium text-sm">{quot.quotation_number}</p>
                            <p className="text-xs text-stone-500">₹{quot.total?.toLocaleString()}</p>
                          </div>
                          <Badge variant="outline">{quot.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* BOQ Worksheets with Discount */}
                {clientPreviewData.boq_worksheets?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                      <FileText size={16} />
                      BOQ Worksheets
                    </h4>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {clientPreviewData.boq_worksheets.map(boq => (
                        <div key={boq.id} className="p-3 bg-gradient-to-r from-amber-50 to-stone-50 rounded-lg border border-amber-200">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-sm">{boq.worksheet_number}</p>
                              <p className="text-xs text-stone-500">{boq.name}</p>
                            </div>
                            <Badge variant="outline" className={boq.status === 'published' ? 'bg-green-100 text-green-700' : ''}>
                              {boq.status}
                            </Badge>
                          </div>
                          <div className="text-xs space-y-1 border-t pt-2 mt-2">
                            <div className="flex justify-between">
                              <span className="text-stone-500">Basic Total:</span>
                              <span>₹{boq.subtotal?.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-stone-500">GST (18%):</span>
                              <span>₹{boq.gst_total?.toLocaleString()}</span>
                            </div>
                            {boq.discount_amount > 0 && (
                              <div className="flex justify-between text-green-600">
                                <span>Discount ({boq.discount_percentage}%):</span>
                                <span>-₹{boq.discount_amount?.toLocaleString()}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-bold text-amber-700 pt-1 border-t">
                              <span>Grand Total:</span>
                              <span>₹{boq.grand_total?.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Invoices */}
                {clientPreviewData.invoices?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                      <FileText size={16} />
                      Recent Invoices
                    </h4>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {clientPreviewData.invoices.slice(0, 5).map(inv => (
                        <div key={inv.id} className="p-3 bg-stone-50 rounded-lg flex justify-between items-center">
                          <div>
                            <p className="font-medium text-sm">{inv.invoice_number}</p>
                            <p className="text-xs text-stone-500">₹{inv.total?.toLocaleString()}</p>
                          </div>
                          <Badge variant="outline" className={inv.status === 'paid' ? 'bg-green-100 text-green-700' : ''}>
                            {inv.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Notifications */}
                {clientPreviewData.recent_notifications?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                      <Bell size={16} />
                      Recent Notifications
                    </h4>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {clientPreviewData.recent_notifications.slice(0, 5).map(notif => (
                        <div key={notif.id} className={`p-3 rounded-lg ${notif.read ? 'bg-stone-50' : 'bg-blue-50 border border-blue-200'}`}>
                          <p className="font-medium text-sm">{notif.title}</p>
                          <p className="text-xs text-stone-500">{notif.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </SheetContent>
        </Sheet>

        {/* Vendor Preview Sheet */}
        <Sheet open={showVendorPreview} onOpenChange={setShowVendorPreview}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <UsersIcon size={20} className="text-purple-600" />
                Preview as Vendor
              </SheetTitle>
              <SheetDescription className="text-stone-500">
                Viewing exactly what {previewVendor?.name || 'vendor'} sees in their profile
              </SheetDescription>
            </SheetHeader>

            {loadingPreview ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : vendorPreviewData ? (
              <div className="mt-6 space-y-6">
                {/* Vendor Info */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-semibold text-purple-900">{vendorPreviewData.vendor?.name}</h3>
                  <p className="text-sm text-purple-700">{vendorPreviewData.vendor?.email}</p>
                  <p className="text-sm text-purple-600">{vendorPreviewData.vendor?.phone}</p>
                </div>

                {/* Assigned Projects */}
                <div>
                  <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                    <Briefcase size={16} />
                    Assigned Projects ({vendorPreviewData.assigned_projects?.length || 0})
                  </h4>
                  {vendorPreviewData.assigned_projects?.length > 0 ? (
                    <div className="space-y-2">
                      {vendorPreviewData.assigned_projects.map(project => (
                        <div key={project.id} className="p-3 bg-stone-50 rounded-lg">
                          <p className="font-medium">{project.name}</p>
                          <p className="text-sm text-stone-500">{project.location}</p>
                          <Badge variant="outline" className="mt-1">{project.status}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-stone-500 italic">No projects assigned</p>
                  )}
                </div>

                {/* Rates Summary */}
                <div>
                  <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                    <Package size={16} />
                    Rates Summary
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-3 bg-stone-50 rounded-lg text-center">
                      <p className="text-lg font-bold">{vendorPreviewData.rates_summary?.total || 0}</p>
                      <p className="text-xs text-stone-500">Total</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-green-600">{vendorPreviewData.rates_summary?.approved || 0}</p>
                      <p className="text-xs text-green-600">Approved</p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg text-center">
                      <p className="text-lg font-bold text-orange-600">{vendorPreviewData.rates_summary?.pending_review || 0}</p>
                      <p className="text-xs text-orange-600">Needs Review</p>
                    </div>
                  </div>
                </div>

                {/* BOQ Worksheets with Discount */}
                {vendorPreviewData.boq_worksheets?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                      <FileText size={16} />
                      BOQ Worksheets
                    </h4>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {vendorPreviewData.boq_worksheets.map(boq => (
                        <div key={boq.id} className="p-3 bg-gradient-to-r from-purple-50 to-stone-50 rounded-lg border border-purple-200">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-sm">{boq.worksheet_number}</p>
                              <p className="text-xs text-stone-500">{boq.name}</p>
                            </div>
                            <Badge variant="outline" className={boq.status === 'published' ? 'bg-green-100 text-green-700' : ''}>
                              {boq.status}
                            </Badge>
                          </div>
                          <div className="text-xs space-y-1 border-t pt-2 mt-2">
                            <div className="flex justify-between">
                              <span className="text-stone-500">Basic Total:</span>
                              <span>₹{boq.subtotal?.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-stone-500">GST (18%):</span>
                              <span>₹{boq.gst_total?.toLocaleString()}</span>
                            </div>
                            {boq.discount_amount > 0 && (
                              <div className="flex justify-between text-green-600">
                                <span>Discount ({boq.discount_percentage}%):</span>
                                <span>-₹{boq.discount_amount?.toLocaleString()}</span>
                              </div>
                            )}
                            <div className="flex justify-between font-bold text-purple-700 pt-1 border-t">
                              <span>Grand Total:</span>
                              <span>₹{boq.grand_total?.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Rates */}
                <div>
                  <h4 className="font-medium text-stone-700 mb-2">Recent Rates</h4>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {vendorPreviewData.rates?.slice(0, 10).map(rate => (
                      <div key={rate.id} className="p-3 bg-stone-50 rounded-lg flex justify-between items-center">
                        <div>
                          <p className="font-medium text-sm">{rate.material_name}</p>
                          <p className="text-xs text-stone-500">₹{rate.rate?.toLocaleString()} / {rate.unit}</p>
                        </div>
                        <Badge variant="outline">
                          {rate.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Notifications */}
                {vendorPreviewData.recent_notifications?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                      <Bell size={16} />
                      Recent Notifications
                    </h4>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {vendorPreviewData.recent_notifications.slice(0, 5).map(notif => (
                        <div key={notif.id} className={`p-3 rounded-lg ${notif.read ? 'bg-stone-50' : 'bg-blue-50 border border-blue-200'}`}>
                          <p className="font-medium text-sm">{notif.title}</p>
                          <p className="text-xs text-stone-500">{notif.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </SheetContent>
        </Sheet>
        <ManagePermissionsDialog targetUser={permUser} onOpenChange={(o) => { if (!o) setPermUser(null); }} />
        {isPlatformOwner && <FoundingAccessPanel open={foundingOpen} onOpenChange={setFoundingOpen} />}
      </div>
    </Layout>
  );
}
